import { inject, Injectable } from '@angular/core';

import { MixpanelTrackerService } from '@/app/core/analytics/mixpanel-tracker.service';
import { AiResponse, AiState } from '@/app/core/analytics/mixpanel-events.types';
import { InquiryCorrespondenceDTO } from '@/app/proxy/proxies';
import { SupportChatBubbleStateService } from './bubble/support-chat-bubble-state.service';

/** Which surface the chat panel is rendered on. */
export type SupportTicketChatSurface = 'bubble' | 'page';

/**
 * Maps the `ui_action` types the stream can emit onto the reported `ai response`.
 * The backend has no signal for `Register` yet - add its action type here once it does.
 */
const AI_RESPONSE_BY_UI_ACTION: Record<string, AiResponse> = {
  'plantation.request_selection': 'Plantation select',
  'inventory.request_selection': 'Device select',
};

/**
 * Everything known about a single AI turn, accumulated while it runs so the
 * `AI Message Received` event can be reported once when the turn settles.
 */
export interface AiTurnRecord {
  chatId: string;
  surface: SupportTicketChatSurface;
  /** Ticket count on the inquiry when the turn started, to detect a ticket being opened. */
  ticketCountBefore: number;
  /** `ui_action` type seen during the turn, if any. */
  uiActionType?: string;
  aiMessageLength: number;
  errored: boolean;
}

@Injectable({ providedIn: 'root' })
export class SupportTicketAnalyticsService {
  private readonly mixpanelTracker = inject(MixpanelTrackerService);
  private readonly bubbleState = inject(SupportChatBubbleStateService);

  trackAiExpanded(): void {
    this.mixpanelTracker.trackAiExpanded();
  }

  trackAiMinimized(): void {
    this.mixpanelTracker.trackAiMinimized();
  }

  trackAiViewed(numberOfChat: number): void {
    this.mixpanelTracker.trackAiViewed({ 'number of chat': numberOfChat });
  }

  trackAiChatViewed(chatId: string): void {
    this.mixpanelTracker.trackAiChatViewed({ 'chat id': chatId });
  }

  trackAiChatDeleted(chatId: string): void {
    this.mixpanelTracker.trackAiChatDeleted({ 'chat id': chatId });
  }

  trackAiMessageSent(properties: {
    message: string;
    previousAiMessageLength: number;
    customMessage: boolean;
    firstMessage: boolean;
    chatId: string;
    photoAdded: boolean;
  }): void {
    this.mixpanelTracker.trackAiMessageSent({
      message: properties.message,
      'user message length': properties.message.length,
      'ai message length': properties.previousAiMessageLength,
      'custom message': properties.customMessage,
      'first message': properties.firstMessage,
      'chat id': properties.chatId,
      'photo added': properties.photoAdded,
    });
  }

  /**
   * @param ticketCountAfter Ticket count once the inquiry has been reconciled with the
   *   server, or `undefined` when the reconcile did not land (the turn is then reported
   *   with whatever signal the stream itself provided).
   */
  trackAiMessageReceived(turn: AiTurnRecord, ticketCountAfter?: number): void {
    this.mixpanelTracker.trackAiMessageReceived({
      'ai message length': turn.aiMessageLength,
      'ai state': this.resolveAiState(turn.surface),
      'ai response': this.resolveAiResponse(turn, ticketCountAfter),
      'chat id': turn.chatId,
      'error - generating response': turn.errored,
    });
  }

  resolveAiState(surface: SupportTicketChatSurface): AiState {
    if (surface === 'page') {
      return 'Support page';
    }
    return this.bubbleState.isOpen ? 'Expanded' : 'Minimized';
  }

  /** Character count of the most recent AI message that actually has content. */
  lastAiMessageLength(correspondences: InquiryCorrespondenceDTO[] | undefined): number {
    const messages = correspondences ?? [];
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (message?.correspondenceType === 'AI' && message.inquiryContent?.trim()) {
        return message.inquiryContent.length;
      }
    }
    return 0;
  }

  private resolveAiResponse(turn: AiTurnRecord, ticketCountAfter?: number): AiResponse {
    if (turn.uiActionType && AI_RESPONSE_BY_UI_ACTION[turn.uiActionType]) {
      return AI_RESPONSE_BY_UI_ACTION[turn.uiActionType];
    }
    if (ticketCountAfter !== undefined && ticketCountAfter > turn.ticketCountBefore) {
      return 'Support ticket';
    }
    return 'Reply';
  }
}
